import React, { useContext } from 'react';
import { getTrackerData } from 'localmed-tracker';

import { SCREENER_STATUSES, ScreenerStatus } from './constants';

import {
  encodeData,
  setSessionStorageItem,
  decodeData,
  getSessionStorageItem,
} from '../../utils/session/utils';
import { savePrefillToken, getPrefillToken } from '../../utils/session/prefillToken';
import { UniversalContext, RENDER_PHASE } from '../../../_universal';

const DEFAULT_SESSION_NAME = 'lm_widget_settings';

// TODO: Define proper types for these objects
export type WidgetSettings = any;
export type TrackerData = any;

export interface ScheduleRequestAttemptData {
  id: string;
  token: string;
  appointmentVerificationToken: string;
  scheduleRequest: {
    id: string;
    contactEmail: string | null;
    contactMobilePhone: string | null;
    patient: {
      givenName: string;
      familyName: string;
      birthdate: string;
      email: string | null;
      mobilePhoneNumber: string | null;
    };
    contactPatient: {
      givenName: string;
      familyName: string;
      birthdate: string;
      email: string | null;
      mobilePhoneNumber: string | null;
    };
  };
}

export interface SchedulingSessionState {
  screenerStatus: ScreenerStatus;
  setScreenerStatus: (status: ScreenerStatus) => void;
  widgetSessionOpenedTracked: boolean;
  setWidgetSessionOpenedTracked: (tracked: boolean) => void;
  widgetSettings: any;
  setWidgetSettings: (widgetSettings: WidgetSettings) => void;
  prefillToken: string | null | undefined;
  setPrefillToken: (token: string) => void;
  trackerData: any;
  setTrackerData: (data: TrackerData) => void;
  scheduleRequestAttemptData?: ScheduleRequestAttemptData;
  setScheduleRequestAttemptData: (data: ScheduleRequestAttemptData) => void;
}

export const DEFAULT_CONTEXT: SchedulingSessionState = {
  screenerStatus: SCREENER_STATUSES.NOT_SCREENED,
  // eslint-disable-next-line no-unused-vars
  setScreenerStatus: () => {},
  widgetSessionOpenedTracked: false,
  // eslint-disable-next-line no-unused-vars
  setWidgetSessionOpenedTracked: () => {},
  widgetSettings: {},
  // eslint-disable-next-line no-unused-vars
  setWidgetSettings: () => {},
  prefillToken: undefined,
  // eslint-disable-next-line no-unused-vars
  setPrefillToken: () => {},
  trackerData: {},
  // eslint-disable-next-line no-unused-vars
  setTrackerData: () => {},
  setScheduleRequestAttemptData: () => {},
};

export const SchedulingSessionStateContext = React.createContext(DEFAULT_CONTEXT);

interface UseSchedulingSessionStateOptions {
  initialWidgetSettings?: any;
  initialPrefillToken?: string | undefined;
  initialTrackerData?: any;
}

export const useSchedulingSessionState = ({
  initialWidgetSettings,
  initialPrefillToken,
  initialTrackerData,
}: UseSchedulingSessionStateOptions = {}): SchedulingSessionState => {
  const [status, setStatus] = React.useState<ScreenerStatus>(SCREENER_STATUSES.NOT_SCREENED);
  const [widgetSessionOpenedTracked, setSessionOpenedTracked] = React.useState<boolean>(false);
  const [widgetSettings, setWidgetSettingsState] = React.useState<WidgetSettings>(
    () =>
      initialWidgetSettings || {
        ...decodeData(getSessionStorageItem(DEFAULT_SESSION_NAME)),
      }
  );

  const [prefillToken, setPrefillTokenState] = React.useState<string | null | undefined>(
    () => initialPrefillToken || getPrefillToken()
  );

  const [trackerData, setTrackerDataState] = React.useState<TrackerData>(
    () => initialTrackerData || getTrackerData()
  );
  const universalContext = useContext(UniversalContext);

  const setScreenerStatus = React.useCallback((updatedStatus) => {
    setStatus(updatedStatus);
  }, []);

  const setWidgetSessionOpenedTracked = React.useCallback((tracked) => {
    setSessionOpenedTracked(tracked);
  }, []);

  const setWidgetSettings = React.useCallback(
    (settings) => {
      setWidgetSettingsState(settings);
      // @ts-ignore TS thinks we’re not handling universalContext as null here.
      if (universalContext && universalContext.renderPhase !== RENDER_PHASE.BEFORE_SERVER_RENDER) {
        setSessionStorageItem(DEFAULT_SESSION_NAME, encodeData(settings));
      }
    },
    [universalContext]
  );

  const setPrefillToken = React.useCallback((token) => {
    setPrefillTokenState(token);
    if (typeof token !== 'undefined') {
      savePrefillToken(token);
    }
  }, []);

  const setTrackerData = React.useCallback((updatedTrackerData) => {
    setTrackerDataState(updatedTrackerData);
  }, []);

  const [
    scheduleRequestAttemptData,
    setScheduleRequestAttemptDataState,
  ] = React.useState<ScheduleRequestAttemptData>();
  const setScheduleRequestAttemptData = React.useCallback(
    (updatedScheduleRequestAttemptData) =>
      setScheduleRequestAttemptDataState(updatedScheduleRequestAttemptData),
    []
  );

  return {
    screenerStatus: status,
    setScreenerStatus,
    widgetSettings,
    setWidgetSettings,
    prefillToken,
    setPrefillToken,
    trackerData,
    setTrackerData,
    widgetSessionOpenedTracked,
    setWidgetSessionOpenedTracked,
    scheduleRequestAttemptData,
    setScheduleRequestAttemptData,
  };
};
